import { Flex, Icon, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { Card } from "../../../components/Card";
import { BackButton } from "../../../components/atoms/Button/BackButton";
import { useNavigate } from "react-router-dom";
import { SelectControl } from "../../../components/templates/Form/SelectControl";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import { api } from "../../../services/api";
import { Input } from "../../../components/Form/Input";
import { useAuth } from "../../../hooks/auth";
import { SubmitButton } from "../../../components/atoms/Button/SubmitButton";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useToast } from "../../../hooks/toast";
import { FaInfoCircle } from "react-icons/fa";
import { MultiSelectControl } from "../../../components/templates/Form/MultiSelectControl";
import { CreateSelectControl } from "../../../components/templates/Form/CreateSelectControl";

interface AccountPlanSubcategory {
  id: string;
  name: string;
  account_plan_category_id: string;
}
interface Favored {
  id: string;
  name: string;
  account_plan_subcategory_id: string;
  account_plan_subcategory: {
    name: string;
  };
}

const createFixedExpenseFormSchema = yup.object().shape({
  observation: yup.string().notRequired(),
  value: yup
    .number()
    .required("Valor é obrigatório")
    .min(0.01, "O valor é R$0,01"),
  form_of_payment: yup.string().required("Forma de pagamento é obrigatória"),
  account_plan_subcategory_id: yup.object().required("Receita é obrigatória"),
  next_date_of_competence: yup.string(),
  first_date_of_competence: yup
    .string()
    .required("Data do competência é obrigatória"),
  favored_id: yup.string().required("O favorecido é obrigatória"),
});

export function CreateFixedExpense() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [accountPlanSubcategories, setAccountPlanSubcategories] = useState<
    AccountPlanSubcategory[]
  >([]);
  const [favored, setFavored] = useState<Favored[]>([]);
  const {
    control,
    register,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(createFixedExpenseFormSchema),
  });

  useEffect(() => {
    api
      .get("/accountPlanSubcategories/type?type=Despesa")
      .then((response) => setAccountPlanSubcategories(response.data));
  }, []);
  watch();

  useEffect(() => {
    api.get(`/favored/company/${user.company_id}`).then((response) => {
      setFavored(response.data);
    });
  }, [user.company_id]);

  const handleSubmitFixedExpense: SubmitHandler<FieldValues> = useCallback(
    async (formValue) => {
      api
        .post(`/fixedExpenses`, {
          account_plan_subcategory_id:
            formValue.account_plan_subcategory_id.value,
          next_date_of_competence: formValue.first_date_of_competence,
          first_date_of_competence: formValue.first_date_of_competence,
          form_of_payment: formValue.form_of_payment,
          favored_name: formValue.favored_name,
          frequency: formValue.frequency,
          observation: formValue.observation,
          value: Number(formValue.value),
        })
        .then((response) => {
          toast({
            title: "Despesa criada com sucesso",
            status: "success",
          });
          navigate("/contas-fixa-pagar");
        })
        .catch((err) => {
          console.log(err);
          toast({
            title: "Não foi possível criar a conta",
            status: "error",
          });
        });
    },
    [navigate, toast]
  );

  return (
    <>
      <Card
        title="Despesa Fixa"
        border="#216ca5"
        header={<BackButton onClick={() => navigate(-1)} />}
        body={
          <Stack
            as="form"
            id="create-fixed-expense"
            onSubmit={handleSubmit(handleSubmitFixedExpense)}
          >
            <SimpleGrid>
              <CreateSelectControl
                onCreateOption={(value: string) => {
                  setValue("favored_name", value, {
                    shouldValidate: true,
                  });
                  setValue("favored_id", value, {
                    shouldValidate: true,
                  });
                  setFavored((prev) => [
                    ...prev,
                    {
                      id: value.toLowerCase(),
                      name: value,
                      account_plan_subcategory_id: "",
                      company_id: user.company_id,
                      account_plan_subcategory: {
                        name: "",
                      },
                    },
                  ]);
                }}
                onChange={(select: any) => {
                  const selected = favored.find((f) => f.id === select.value);
                  if (selected) {
                    setValue("favored_id", selected.id, {
                      shouldValidate: true,
                    });
                    setValue("favored_name", selected.name);
                    if (selected.account_plan_subcategory.name) {
                      setValue("account_plan_subcategory_id", {
                        label: selected.account_plan_subcategory.name,
                        value: selected.account_plan_subcategory_id,
                      });
                    }
                  }
                }}
                options={favored.map((f) => ({
                  label: `${f.name} ${
                    f.account_plan_subcategory.name
                      ? `- ${f.account_plan_subcategory.name}`
                      : ""
                  }`,
                  value: f.id,
                }))}
                name="favored"
                label="Favorecido"
                isRequired
                value={
                  getValues("favored_id")
                    ? {
                        label: getValues("favored_name"),
                        value: getValues("favored_id"),
                      }
                    : ""
                }
                error={errors.favored_id}
                validate={getValues("favored_name") && !errors.favored}
              />

              <MultiSelectControl
                options={accountPlanSubcategories.map((acc) => ({
                  label: acc.name,
                  value: acc.id,
                }))}
                {...register("account_plan_subcategory_id")}
                onChange={(selected) => {
                  setValue("account_plan_subcategory_id", selected, {
                    shouldValidate: true,
                  });
                }}
                value={getValues("account_plan_subcategory_id")}
                validaded={
                  getValues("account_plan_subcategory_id") &&
                  getValues("account_plan_subcategory_id").value !== "" &&
                  !errors.account_plan_subcategory_id
                }
                isMulti={false}
                label="Despesa"
                error={errors.account_plan_subcategory_id}
                isRequired={true}
              />
              <Input
                type="text"
                label="Descrição"
                isRequired={false}
                validaded={
                  getValues("observation") !== "" && !errors.observation
                }
                error={errors.observation}
                {...register("observation")}
              />
            </SimpleGrid>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={4}>
              <SimpleGrid borderRadius={8} boxShadow="md" py="4" px="8">
                <SelectControl
                  control={control}
                  error={errors.frequency}
                  {...register("frequency")}
                  validaded={getValues("frequency") && getValues("frequency") !== ""}
                  isRequired={true}
                  label="Periodecidade"
                  options={[
                    {
                      id: "diaria",
                      name: "Diária",
                    },
                    {
                      id: "semanal",
                      name: "Semanal",
                    },
                    {
                      id: "mensal",
                      name: "Mensal",
                    },
                  ]}
                />
                <SelectControl
                  control={control}
                  error={errors.form_of_payment}
                  {...register("form_of_payment")}
                  validaded={getValues("form_of_payment") && getValues("form_of_payment") !== ""}
                  isRequired={true}
                  label="Forma de Pagamento"
                  options={[
                    {
                      id: "boleto",
                      name: "Boleto",
                    },
                    {
                      id: "caixa",
                      name: "Fundo de Caixa",
                    },
                    {
                      id: "credito",
                      name: "Cartão de crédito",
                    },
    
                    {
                      id: "debito",
                      name: "Cartão de débito",
                    },
                    {
                      id: "dinheiro",
                      name: "Dinheiro",
                    },
                    {
                      id: "pix",
                      name: "Pix",
                    },
                    {
                      id: "transferencia",
                      name: "Transferência",
                    },
                  ]}
                />
                <Input
                  label="Valor"
                  isRequired={true}
                  error={errors.value}
                  validaded={getValues("value") && getValues("value") !== 0 && !errors.value}
                  {...register("value")}
                  leftAddon="R$"
                  onChange={(e) => {
                    var value = e.target.value;
                    if (value.length > 2) {
                      value = value
                        .replace(/\D+/g, "")
                        .replace(/([0-9]{2})$/g, ",$1");
                      value = Number(value.replace(",", ".")).toFixed(2);
                      setValue("value", Number(value), {
                        shouldValidate: true,
                      });
                      e.target.value = value;
                    } else if (value === "" || value === "0.00") {
                      setValue("value", 0, {
                        shouldValidate: true,
                      });
                    } else {
                      value = (Number(value) / 100).toString();
                      value = value
                        .replace(/\D+/g, "")
                        .replace(/([0-9]{2})$/g, ",$1");
                      value = Number(value.replace(",", ".")).toString();
                      setValue("value", Number(value), {
                        shouldValidate: true,
                      });
                      e.target.value = value.replace(".", ",");
                    }
                  }}
                />
              </SimpleGrid>
              <SimpleGrid
                bg="#f0f0f0"
                borderRadius={8}
                boxShadow="md"
                py="4"
                px="8"
              >
                <Input
                  type="date"
                  label="Data da Primeira Competência"
                  isRequired={true}
                  validaded={getValues("first_date_of_competence") !== ""}
                  error={errors.first_date_of_competence}
                  {...register("first_date_of_competence")}
                />

                <Flex
                  alignItems={"center"}
                  bg="#FFFFB4"
                  px="4"
                  borderRadius={8}
                  boxShadow="md"
                >
                  <Icon as={FaInfoCircle} mr="2" />
                  <Text>
                    Caso cadastre uma data passada, serão geradas
                    automaticamente contas referente a periodicidade selecionada
                    até o dia de hoje.
                  </Text>
                </Flex>
              </SimpleGrid>
            </SimpleGrid>
          </Stack>
        }
        footer={<SubmitButton form="create-fixed-expense" />}
      />
    </>
  );
}
